import { Blocks } from "@/components/cms/Blocks";
import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { linkSlot, visibleItems } from "@/components/cms/SectionRenderer";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { ArrowRight, ButtonLink, TextLink } from "@/components/ui/Button";
import { ExternalLink } from "@/components/ui/ExternalLink";
import { MediaFigure } from "@/components/ui/MediaFigure";
import { PageHeader } from "@/components/ui/PageHeader";
import { PartnerStrip } from "@/components/ui/PartnerStrip";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useMediaByKey, useSettings } from "@/content-context";
import { buildMetadata } from "@/lib/seo";
import { imageReveal } from "@/lib/motion";
import { pageLoader, usePageSection } from "@/page-data";

/**
 * Support our work. This is what "Partner with us" became.
 *
 * The old page addressed one audience and the route served seven. A foundation,
 * an embassy, a CSR team, an individual supporter and somebody in the diaspora
 * all arrive wanting to know the same three things: what the money does, what
 * the organisation can evidence, and who to talk to.
 *
 * /get-involved/partner still resolves. It redirects here, because inbound
 * links, search results and documents HISCUS has already sent out all point at
 * the old address.
 *
 * There is deliberately no donate button. It appears when a verified payment
 * destination, receipting and the legal basis for it exist, and not before: a
 * checkout that does not work costs more trust than no checkout at all.
 */

export const loader = pageLoader("support");

export const meta = () =>
  buildMetadata({
    title: "Support our work",
    description:
      "Fund, partner with or sponsor HISCUS Nepal: institutional partnership, CSR, programme sponsorship, volunteering and Charity Trek.",
    path: "/get-involved/support",
  });

function AudiencesSection({ section }: SectionProps) {
  const ways = visibleItems(section);
  if (!ways.length) return null;
  return (
    <Section ariaLabelledBy="ways-heading">
      <SectionHeading id="ways-heading" eyebrow={section.eyebrow} title={section.title || "Ways to work with HISCUS"} />
      <RevealGroup as="ul" className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {ways.map((way) => (
          <Reveal as="li" key={way.title}>
            <div className="surface group relative flex h-full flex-col rounded-[16px] p-6">
              <h3 style={{ fontSize: "var(--step-h4)" }}>{way.title}</h3>
              {way.text ? <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{way.text}</p> : null}
              {way.href ? (
                <span className="mt-auto pt-5">
                  <TextLink href={way.href} stretch>
                    {way.label || "Read more"}
                  </TextLink>
                </span>
              ) : null}
            </div>
          </Reveal>
        ))}
      </RevealGroup>
    </Section>
  );
}

/**
 * What support funds, with what HISCUS brings beside it.
 *
 * The "What HISCUS brings" panel is its own section in the editor, so it can be
 * edited or hidden on its own, and it is drawn here, beside the units of work.
 */
function FundsSection({ section }: SectionProps) {
  const settings = useSettings();
  const byKey = useMediaByKey();
  const capability = usePageSection("support.capability");
  const capabilityImage = byKey(capability?.mediaKey);

  return (
    <Section tone="ivory" ariaLabelledBy="funds-heading">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading
            id="funds-heading"
            eyebrow={section.eyebrow}
            title={section.title || "Units of work, with the real figures attached"}
            lead={section.lead}
          />

          <dl className="mt-9 space-y-5">
            {visibleItems(section).map((entry) => (
              <div key={entry.title} className="border-b border-line pb-5 last:border-0 last:pb-0">
                <dt className="font-serif text-[1.0625rem] text-midnight">{entry.title}</dt>
                <dd className="mt-1.5 text-[0.9375rem] leading-relaxed text-muted">{entry.text}</dd>
              </div>
            ))}
          </dl>

          {settings.support.donationEnabled && settings.support.donationUrl ? (
            <div className="mt-9">
              <ExternalLink href={settings.support.donationUrl}>Give to HISCUS</ExternalLink>
            </div>
          ) : settings.support.note ? (
            <div className="notice notice-amber mt-9" role="note">
              <p className="text-[0.9375rem] text-ink">{settings.support.note}</p>
            </div>
          ) : null}
        </Reveal>

        {capability ? (
          <Reveal>
            <div className="surface rounded-[20px] p-7 md:p-9">
              {capability.eyebrow ? (
                <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">{capability.eyebrow}</h3>
              ) : null}
              {capability.title ? <p className="mt-3 font-serif text-[1.375rem] text-midnight">{capability.title}</p> : null}
              <ul className="mt-6 space-y-4">
                {visibleItems(capability).map((item) => (
                  <li key={item.title} className="flex gap-3.5 text-[0.9375rem] leading-relaxed text-muted">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue" />
                    <span>
                      <strong className="text-ink">{item.title}.</strong> {item.text}
                    </span>
                  </li>
                ))}
              </ul>

              <dl className="mt-8 grid gap-3 sm:grid-cols-3">
                {settings.registrations.map((reg) => (
                  <div key={reg.value} className="rounded-[14px] border border-line bg-mist p-4">
                    <dt className="text-[0.8125rem] leading-snug text-muted">{reg.label}</dt>
                    <dd className="mt-1.5 font-serif text-[1.125rem] text-midnight">{reg.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {capabilityImage ? (
              <div className="mt-5">
                <MediaFigure item={capabilityImage} ratio="16 / 10" sizes="(min-width: 1024px) 45vw, 100vw" showCaption={false} radius={20} />
              </div>
            ) : null}
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

/** Drawn inside "What support funds". */
function CapabilitySection() {
  return null;
}

function PartnersSection({ section }: SectionProps) {
  const byKey = useMediaByKey();
  const image = byKey(section.mediaKey);
  return (
    <Section ariaLabelledBy="partners-heading">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <SectionHeading id="partners-heading" eyebrow={section.eyebrow} title={section.title || "Who HISCUS already works with"} />
          <div className="mt-8">
            <PartnerStrip />
          </div>
        </Reveal>

        {image ? (
          <Reveal variants={imageReveal}>
            <MediaFigure item={image} ratio="4 / 3" sizes="(min-width: 1024px) 45vw, 100vw" />
          </Reveal>
        ) : null}
      </div>
    </Section>
  );
}

function DueDiligenceSection({ section }: SectionProps) {
  const button = linkSlot(section, 0);
  return (
    <Section tone="mist" ariaLabelledBy="due-diligence-heading">
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:gap-16">
        <Reveal>
          <SectionHeading id="due-diligence-heading" eyebrow={section.eyebrow} title={section.title || "What we can send you"} />
          <Blocks blocks={section.body} className="mt-6" />

          {button ? (
            <div className="mt-7 flex flex-wrap gap-3">
              <ButtonLink href={button.href} variant="secondary">
                {button.label}
                <ArrowRight />
              </ButtonLink>
            </div>
          ) : null}
        </Reveal>

        <Reveal>
          <EnquiryForm heading={section.texts?.formHeading || "Start a support conversation"} defaultInterest="support" />
        </Reveal>
      </div>
    </Section>
  );
}

export const supportSections: SectionRenderers = {
  "support.audiences": AudiencesSection,
  "support.funds": FundsSection,
  "support.capability": CapabilitySection,
  "support.partners": PartnersSection,
  "support.due-diligence": DueDiligenceSection,
};

export function SupportPage() {
  return (
    <>
      <PageHeader
        eyebrow="Support our work"
        title="Fund the work, or deliver it alongside us"
        lead="HISCUS works with funders and institutions that need programmes delivered well in Nepali communities, and reported honestly afterwards. Individual supporters fund the same delivery."
        trail={[
          { name: "Home", href: "/" },
          { name: "Get Involved", href: "/get-involved" },
          { name: "Support our work", href: "/get-involved/support" },
        ]}
      />
      <PageSections renderers={supportSections} />
    </>
  );
}
